import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ChevronRight, RefreshCcw } from "lucide-react";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { formatDistanceKm, formatDurationHm, formatDate, todayLocal } from "../../lib/utils";
import { Skeleton } from "../../components/ui/skeleton";
import { ErrorState } from "../../components/shared/ErrorState";
import { cacheToday, getCachedToday } from "../../lib/driverSync";
import { SyncIndicator } from "../../components/driver/SyncIndicator";

const HISTORY_CHUNK_DAYS = 120;
const HISTORY_FLOOR_DAYS = 1095; // stop paging ~3 years back

function routeStatusLabel(r) {
  if (r.finished_at) return { label: "Finished", color: "bg-deactivated-bg text-deactivated" };
  if (r.started_at) return { label: "In progress", color: "bg-success-bg text-success" };
  return { label: "Not started", color: "bg-warning-bg text-warning" };
}

function shiftDate(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function RouteCard({ route, navigate, testIdPrefix }) {
  const meta = routeStatusLabel(route);
  return (
    <button
      type="button"
      onClick={() => navigate(`/driver/routes/${route.id}`)}
      className="flex w-full items-center justify-between rounded-md border border-border bg-surface p-4 text-left shadow-sm transition-colors active:bg-background"
      data-testid={`${testIdPrefix}-${route.id}`}
    >
      <div>
        <p className="text-sm font-semibold text-text-main">Route #{route.id}</p>
        <p className="mt-1 flex items-center gap-1 text-xs text-text-muted">
          <MapPin size={12} strokeWidth={1.5} /> {route.deliveries_done}/{route.deliveries_total} stops · {formatDistanceKm(route.distance)} · {formatDurationHm(route.duration)}
        </p>
        <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.color}`} data-testid={`driver-route-status-${route.id}`}>
          {meta.label}
        </span>
      </div>
      <ChevronRight size={20} strokeWidth={1.5} className="text-text-muted" />
    </button>
  );
}

export default function DriverRoutes() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [state, setState] = useState({ loading: true, error: null, items: [], date: null });
  const [history, setHistory] = useState({ loading: false, error: null, groups: [], cursor: null, exhausted: false, started: false });

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/driver/routes")
      .then(({ data }) => {
        cacheToday(data);
        setState({ loading: false, error: null, items: data.items, date: data.date });
      })
      .catch(async (error) => {
        const cached = await getCachedToday();
        if (cached) {
          setState({ loading: false, error: null, items: cached.items, date: cached.date, offline: true });
        } else {
          setState({ loading: false, error: getApiErrorMessage(error, "Could not load your routes."), items: [], date: null });
        }
      });
  };

  const loadMoreHistory = () => {
    const to = history.cursor || shiftDate(todayLocal(), -1);
    const from = shiftDate(to, -(HISTORY_CHUNK_DAYS - 1));
    const floor = shiftDate(todayLocal(), -HISTORY_FLOOR_DAYS);
    setHistory((h) => ({ ...h, loading: true, started: true }));
    apiClient
      .get("/driver/routes", { params: { date_from: from, date_to: to } })
      .then(({ data }) => {
        const byDate = new Map();
        data.items.forEach((r) => {
          if (!byDate.has(r.date)) byDate.set(r.date, []);
          byDate.get(r.date).push(r);
        });
        const newGroups = [...byDate.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
        const reachedFloor = from <= floor;
        setHistory((h) => {
          const groups = [...h.groups, ...newGroups];
          const next = {
            loading: false, error: null, groups,
            cursor: shiftDate(from, -1), exhausted: reachedFloor, started: true,
          };
          // Auto-page backwards through empty windows until we surface the first
          // batch of routes (some drivers' most recent routes are >30 days old),
          // stopping at the history floor.
          if (groups.length === 0 && !reachedFloor) {
            setTimeout(loadMoreHistoryRef.current, 0);
            return { ...next, loading: true };
          }
          return next;
        });
      })
      .catch((error) => setHistory((h) => ({ ...h, loading: false, error: getApiErrorMessage(error, "Could not load past routes.") })));
  };
  const loadMoreHistoryRef = useRef(loadMoreHistory);
  loadMoreHistoryRef.current = loadMoreHistory;

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (tab === "history" && !history.started) loadMoreHistory();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="driver-routes-page">
      <SyncIndicator />
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-xl font-semibold text-text-main">My Routes</h1>
        {tab === "today" && (
          <button type="button" onClick={load} className="rounded-md p-2 text-text-muted hover:bg-border/50" data-testid="driver-routes-refresh-button">
            <RefreshCcw size={16} strokeWidth={1.5} />
          </button>
        )}
      </div>

      <div className="mb-4 flex rounded-md border border-border bg-surface p-1" data-testid="driver-routes-tabs">
        <button
          type="button" onClick={() => setTab("today")}
          className={`flex-1 rounded-sm py-1.5 text-xs font-medium transition-colors ${tab === "today" ? "bg-primary text-white" : "text-text-muted"}`}
          data-testid="driver-routes-tab-today"
        >
          Today
        </button>
        <button
          type="button" onClick={() => setTab("history")}
          className={`flex-1 rounded-sm py-1.5 text-xs font-medium transition-colors ${tab === "history" ? "bg-primary text-white" : "text-text-muted"}`}
          data-testid="driver-routes-tab-history"
        >
          Previous Routes
        </button>
      </div>

      {tab === "today" && (
        state.loading ? (
          <>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="mt-3 h-24 w-full" />
          </>
        ) : state.error ? (
          <ErrorState message={state.error} onRetry={load} />
        ) : (
          <>
            <p className="mb-3 text-xs text-text-muted">{formatDate(state.date)}</p>
            {state.items.length === 0 && (
              <div className="rounded-md border border-dashed border-border bg-surface py-12 text-center text-sm text-text-muted" data-testid="driver-routes-empty">
                No routes assigned to you for today.
              </div>
            )}
            <div className="space-y-3">
              {state.items.map((r) => (
                <RouteCard key={r.id} route={r} navigate={navigate} testIdPrefix="driver-route-card" />
              ))}
            </div>
          </>
        )
      )}

      {tab === "history" && (
        <div data-testid="driver-routes-history">
          {history.error && <ErrorState message={history.error} onRetry={loadMoreHistory} />}
          {!history.error && history.groups.length === 0 && !history.loading && (
            <div className="rounded-md border border-dashed border-border bg-surface py-12 text-center text-sm text-text-muted" data-testid="driver-history-empty">
              No previous routes found.
            </div>
          )}
          <div className="space-y-5">
            {history.groups.map(([date, routes]) => (
              <div key={date} data-testid={`driver-history-group-${date}`}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">{formatDate(date)}</p>
                <div className="space-y-3">
                  {routes.map((r) => (
                    <RouteCard key={r.id} route={r} navigate={navigate} testIdPrefix="driver-history-route-card" />
                  ))}
                </div>
              </div>
            ))}
          </div>
          {history.loading && (
            <>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="mt-3 h-24 w-full" />
            </>
          )}
          {!history.loading && !history.exhausted && history.started && (
            <button
              type="button" onClick={loadMoreHistory}
              className="mt-4 w-full rounded-md border border-border bg-surface py-2.5 text-xs font-medium text-text-main active:bg-background"
              data-testid="driver-history-load-more-button"
            >
              Load Older Routes
            </button>
          )}
          {history.exhausted && history.groups.length > 0 && (
            <p className="mt-4 text-center text-xs text-text-muted" data-testid="driver-history-exhausted-label">That's all your previous routes.</p>
          )}
        </div>
      )}
    </div>
  );
}
